import { getFullImageUrl, getThumbnailUrl } from "../../utils/urlConfig";
import React, { useState, useEffect } from "react";
import Modal from "react-modal";
import { fetchLocationDetailsById, uploadLocationImage, setPrimaryLocationImage, deleteLocationImage } from "../../services/locationService";
import { toast } from 'react-toastify';
import styles from "./LocationModal.module.css";
import ActivityHistoryModal from '../Activity/ActivityHistoryModal';
import { FaUpload, FaStar, FaTrash, FaCopy, FaCheck, FaShareAlt, FaExternalLinkAlt, FaBoxes, FaMapMarkerAlt, FaHistory } from 'react-icons/fa';
import QRCode from "react-qr-code";

/**
 * LocationModal Component
 * Displays comprehensive location details, including parent hierarchy, sub-locations, and assets.
 */
const LocationModal = ({ isOpen, onClose, locationId }) => {
    const [locationDetails, setLocationDetails] = useState(null);
    const [loading, setLoading] = useState(false);
    const [imageSrc, setImageSrc] = useState("");
    const [showOverlay, setShowOverlay] = useState(false);
    const [copied, setCopied] = useState(false);
    const [historyModalOpen, setHistoryModalOpen] = useState(false);

    /**
     * Handles location image loading errors.
     */
    const handleImageError = () => {
        setImageSrc("");
    };

    const handleSetPrimary = async (imageId) => {
        try {
            await setPrimaryLocationImage(imageId);
            const updatedRes = await fetchLocationDetailsById(locationId);
            setLocationDetails(updatedRes.data);
            const newPrimary = updatedRes.data.imageDetails?.find(img => img.isPrimary);
            if (newPrimary) setImageSrc(newPrimary.imagePath);
            toast.success("Primary image updated successfully");
        } catch (err) {
            console.error("Error setting primary image", err);
            toast.error("Failed to set primary image");
        }
    };

    const handleDeleteImage = async (imageId) => {
        if (!window.confirm("Are you sure you want to delete this image?")) return;
        try {
            await deleteLocationImage(imageId);
            const updatedRes = await fetchLocationDetailsById(locationId);
            setLocationDetails(updatedRes.data);
            if (updatedRes.data.imageDetails && updatedRes.data.imageDetails.length > 0) {
                const primary = updatedRes.data.imageDetails.find(img => img.isPrimary);
                setImageSrc(primary ? primary.imagePath : updatedRes.data.imageDetails[0].imagePath);
            } else if (updatedRes.data.images && updatedRes.data.images.length > 0) {
                setImageSrc(updatedRes.data.images[0]);
            } else {
                setImageSrc("");
            }
            toast.success("Image deleted successfully");
        } catch (err) {
            console.error("Error deleting image", err);
            toast.error("Failed to delete image");
        }
    };

    const copyToClipboard = () => {
        const identifier = locationDetails?.qrToken || locationId;
        const publicUrl = `${window.location.origin}/public/locations/${identifier}`;
        navigator.clipboard.writeText(publicUrl).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
            toast.success("Link copied to clipboard");
        });
    };

    const handleImageUpload = async (event) => {
        const files = event.target.files;

        if (!files || files.length === 0) {
            return;
        }

        const validFiles = Array.from(files).filter((file) => file.type.startsWith("image"));
        if (validFiles.length === 0) {
            toast.error("Please upload valid image files.");
            return;
        }

        try {
            const result = await uploadLocationImage(locationId, validFiles);
            if (result.success) {
                console.log("Images uploaded successfully:", result.data);
                const updatedRes = await fetchLocationDetailsById(locationId);
                setLocationDetails(updatedRes.data);
                if (updatedRes.data.imageDetails && updatedRes.data.imageDetails.length > 0) {
                    const primary = updatedRes.data.imageDetails.find(img => img.isPrimary);
                    const lastDetail = updatedRes.data.imageDetails[updatedRes.data.imageDetails.length - 1];
                    // If a primary exists, show it, otherwise show the newly uploaded one (last)
                    setImageSrc(primary ? primary.imagePath : lastDetail.imagePath);
                } else if (updatedRes.data.images && updatedRes.data.images.length > 0) {
                    setImageSrc(updatedRes.data.images[updatedRes.data.images.length - 1]);
                }
                toast.success("Images uploaded successfully");
            } else {
                console.error("Image upload failed:", result.error);
                toast.error("Image upload failed");
            }
        } catch (error) {
            console.error("Error while uploading images:", error);
            toast.error("Error while uploading images");
        }
    };

    useEffect(() => {
        if (isOpen && locationId) {
            setLocationDetails(null);
            setImageSrc("");
            setLoading(true);
            fetchLocationDetailsById(locationId)
                .then((res) => {
                    setLocationDetails(res.data);
                    if (res.data.imageDetails && res.data.imageDetails.length > 0) {
                        const primary = res.data.imageDetails.find(img => img.isPrimary);
                        setImageSrc(primary ? primary.imagePath : res.data.imageDetails[0].imagePath);
                    } else if (res.data.images && res.data.images.length > 0) {
                        // Fallback for any legacy response structure
                        setImageSrc(res.data.images[0]);
                    } else if (res.data.image) {
                        setImageSrc(res.data.image);
                    } else {
                        setImageSrc("");
                    }
                })
                .catch((err) => {
                    console.error("Error fetching location details:", err);
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    }, [isOpen, locationId]);

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onClose}
            className={styles.modal}
            overlayClassName={styles["modal-overlay"]}
        >
            <div className={styles["modal-header"]}>
                <h2>Container Details</h2>
                <button className={styles["close-button"]} onClick={onClose}>
                    &times;
                </button>
            </div>

            <div className={styles["modal-content"]}>
                {loading ? (
                    <p className={styles.loading}>Loading container details...</p>
                ) : locationDetails ? (
                    <>
                        <div className={styles["flex-row"]}>

                            {/* Left Column: Visual Identity */}
                            <div className={styles["flex-column"]}>
                                <div className={styles["visual-identity"]}>
                                    <div className={styles["image-frame"]}>
                                        {imageSrc ? (
                                            <img
                                                src={getFullImageUrl(imageSrc)}
                                                alt="Container"
                                                className={styles["location-image"]}
                                                onError={handleImageError}
                                            />
                                        ) : (
                                            <div className={styles["no-image"]}>No image available</div>
                                        )}

                                        <div className={styles.overlay}>
                                            <label htmlFor="file-upload" className={styles.iconButton} title="Upload Images">
                                                <FaUpload />
                                                <input
                                                    type="file"
                                                    id="file-upload"
                                                    multiple
                                                    accept="image/*"
                                                    className={styles.hiddenInput}
                                                    onChange={handleImageUpload}
                                                />
                                            </label>
                                        </div>
                                    </div>

                                    {/* Location Image Gallery */}
                                    {locationDetails?.imageDetails && locationDetails.imageDetails.length > 0 ? (
                                        <div className={styles["image-gallery"]}>
                                            {locationDetails.imageDetails.map((img) => (
                                                <div key={img.id} className={`${styles.galleryItemWrapper} ${imageSrc === img.imagePath ? styles.activeWrapper : ""}`}>
                                                    <img
                                                        src={getThumbnailUrl(img.thumbnailPath || img.imagePath)}
                                                        alt={`Thumbnail ${img.id}`}
                                                        className={styles["gallery-thumbnail"]}
                                                        onClick={() => setImageSrc(img.imagePath)}
                                                    />
                                                    <div className={styles.galleryControls}>
                                                        <button
                                                            className={`${styles.controlBtn} ${img.isPrimary ? styles.primaryActive : ""}`}
                                                            onClick={(e) => { e.stopPropagation(); handleSetPrimary(img.id); }}
                                                            title="Set as Main Image"
                                                            style={{ color: img.isPrimary ? "#ffc107" : "white" }}
                                                        >
                                                            <FaStar />
                                                        </button>
                                                        <button
                                                            className={styles.controlBtn}
                                                            onClick={(e) => { e.stopPropagation(); handleDeleteImage(img.id); }}
                                                            title="Delete Image"
                                                        >
                                                            <FaTrash />
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        // Fallback for legacy locations without imageDetails
                                        locationDetails?.images && locationDetails.images.length > 0 && (
                                            <div className={styles["image-gallery"]}>
                                                {locationDetails.images.map((img, index) => (
                                                    <img
                                                        key={index}
                                                        src={getThumbnailUrl(img)}
                                                        alt={`Location ${index}`}
                                                        className={`${styles["gallery-thumbnail"]} ${imageSrc === img ? styles["active-thumbnail"] : ""}`}
                                                        onClick={() => setImageSrc(img)}
                                                    />
                                                ))}
                                            </div>
                                        )
                                    )}

                                    {/* Public Share Link Container */}
                                    <div className={styles["share-container"]}>
                                        <span className={styles["qr-label"]}><FaShareAlt style={{ marginRight: '6px' }} /> Public Share Link</span>
                                        <div className={styles.shareUrlWrapper}>
                                            <input
                                                type="text"
                                                readOnly
                                                className={styles.shareInput}
                                                value={`${window.location.origin}/public/locations/${locationDetails?.qrToken || locationId}`}
                                                onClick={(e) => e.target.select()}
                                            />
                                            <button
                                                className={`${styles.copyBtn} ${copied ? styles.copied : ""}`}
                                                onClick={copyToClipboard}
                                                title="Copy Link"
                                            >
                                                {copied ? <FaCheck /> : <FaCopy />}
                                            </button>
                                            <a
                                                href={`${window.location.origin}/public/locations/${locationId}`}
                                                target="_blank"
                                                rel="noreferrer"
                                                className={styles.externalBtn}
                                                title="Open Public Page"
                                            >
                                                <FaExternalLinkAlt />
                                            </a>
                                        </div>
                                    </div>

                                    {/* QR Code Container */}
                                    {locationDetails?.qrToken ? (
                                        <div className={styles["qr-container"]}>
                                            <span className={styles["qr-label"]}>Registered QR Token</span>
                                            <div style={{ background: 'white', padding: '10px', borderRadius: '8px', marginTop: '10px', textAlign: 'center', border: '1px solid var(--border-color)' }}>
                                                <QRCode 
                                                    value={`${window.location.origin}/scan/${locationDetails.qrToken}`} 
                                                    size={150} 
                                                    style={{ maxWidth: '100%', height: 'auto' }}
                                                />
                                                <div style={{ fontSize: '13px', fontWeight: 'bold', marginTop: '8px', color: 'var(--primary-color)' }}>
                                                    {locationDetails.qrToken}
                                                </div>
                                            </div>
                                        </div>
                                    ) : locationDetails.qrCode ? (
                                        <div className={styles["qr-container"]}>
                                            <span className={styles["qr-label"]}>Container ID Mapping</span>
                                            <img
                                                src={getFullImageUrl(locationDetails.qrCode)}
                                                alt="QR Code"
                                                className={styles["qr-image"]}
                                            />
                                        </div>
                                    ) : null}
                                </div>
                            </div>

                            {/* Right Column: Detailed Information */}
                            <div className={styles["flex-column-details"]}>
                                <div className={styles["location-info"]}>
                                    <h3>{locationDetails.name || "-"}</h3>
                                    <p className={styles.description}>{locationDetails.description || "-"}</p>

                                    {/* Identification Card */}
                                    <div className={styles["details-section"]}>
                                        <h4>Identification</h4>
                                        <div className={styles["info-grid"]}>
                                            <div className={styles["info-item"]}>
                                                <strong>Container ID</strong>
                                                <span className={styles.value}>{locationDetails.id || "-"}</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Hierarchy Card */}
                                    <div className={styles["details-section"]}>
                                        <h4>Hierarchy</h4>
                                        <div className={styles["info-grid"]}>
                                            <div className={styles["info-item"]}>
                                                <strong>Parent Container</strong>
                                                <span className={styles.value}>
                                                    {locationDetails.parentLocation ? `${locationDetails.parentLocation.name} (${locationDetails.parentLocation.id})` : "-"}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Contents Card: Assets */}
                                    <div className={styles["details-section"]}>
                                        <h4>Contained Assets</h4>
                                        <div className={styles["content-list"]}>
                                            {locationDetails.assets && locationDetails.assets.length > 0 ? (
                                                locationDetails.assets.map((asset) => (
                                                    <div key={asset.id} className={styles["content-item"]}>
                                                        <div className={styles["item-name"]}>
                                                            <FaBoxes style={{ marginRight: '8px', color: 'var(--primary-color)' }} />
                                                            {asset.name}
                                                        </div>
                                                        <div className={styles["item-type"]}>Asset ({asset.id})</div>
                                                    </div>
                                                ))
                                            ) : (
                                                <p style={{ fontStyle: 'italic', color: 'var(--text-muted)', margin: 0 }}>No direct assets mapped to this container.</p>
                                            )}
                                        </div>
                                    </div>

                                    {/* Contents Card: Sub-locations */}
                                    <div className={styles["details-section"]}>
                                        <h4>Sub-Containers</h4>
                                        <div className={styles["content-list"]}>
                                            {locationDetails.childLocations && locationDetails.childLocations.length > 0 ? (
                                                locationDetails.childLocations.map((child) => (
                                                    <div key={child.id} className={styles["content-item"]}>
                                                        <div className={styles["item-name"]}>
                                                            <FaMapMarkerAlt style={{ marginRight: '8px', color: 'var(--primary-color)' }} />
                                                            {child.name}
                                                        </div>
                                                        <div className={styles["item-type"]}>Container ({child.id})</div>
                                                    </div>
                                                ))
                                            ) : (
                                                <p style={{ fontStyle: 'italic', color: 'var(--text-muted)', margin: 0 }}>No sub-containers found within this container.</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Container History Button */}
                        <div style={{ width: '100%', marginTop: '30px', display: 'flex', justifyContent: 'center' }}>
                            <button 
                                className={styles.viewHistoryBtn}
                                onClick={() => setHistoryModalOpen(true)}
                                style={{ 
                                    background: 'var(--glass-bg)', 
                                    border: '1px solid var(--border-color)', 
                                    color: 'var(--primary-color)',
                                    padding: '12px 24px',
                                    borderRadius: 'var(--radius-md)',
                                    fontWeight: '700',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px'
                                }}
                            >
                                <FaHistory /> View Activity History
                            </button>
                        </div>
                    </>
                ) : (
                    <p className={styles["no-data"]}>No details found for this container.</p>
                )}
            </div>

            <ActivityHistoryModal 
                isOpen={historyModalOpen}
                onRequestClose={() => setHistoryModalOpen(false)}
                entityType="LOCATION"
                entityId={locationId}
                entityName={locationDetails?.name}
            />
        </Modal>
    );
};

export default LocationModal;
